import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Grid, IconButton } from '@mui/material';
import Modal from 'components/Modal/Modal';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalContextState } from '../../contexts/globalContext';
import useGlobalContext from '../../hooks/useGlobalContext';
import { GTM, GTMEvents } from '../../utils/analytics';
import { PhoneInputData } from '../PhoneInput/PhoneInput';
import { UserPhoneVerificationSendCode } from './components/UserPhoneVerificationSendCode';
import { UserPhoneVerificationSuccess } from './components/UserPhoneVerificationSuccess';
import { UserPhoneVerificationVerifyCode } from './components/UserPhoneVerificationVerifyCode';

export enum UserPhoneVerificationType {
  CHANGE_PHONE_VERIFY_CURRENT_NUMBER = 'CHANGE_PHONE_VERIFY_CURRENT_NUMBER',
  CHANGE_PHONE_VERIFY_NEW_NUMBER = 'CHANGE_PHONE_VERIFY_NEW_NUMBER',
  VERIFY_NEW_PHONE = 'VERIFY_NEW_PHONE',
  VERIFY_ACTION = 'VERIFY_ACTION',
}

export type OtpTriggerSource = 'purchase' | 'add_phone' | 'change_phone_old' | 'change_phone_new' | 'listing';

enum View {
  SEND_CODE = 'SEND_CODE',
  VERIFY_CODE = 'VERIFY_CODE',
  DONE = 'DONE',
}

export const UserPhoneVerificationModal = () => {
  const { get, set } = useGlobalContext();
  const phoneVerificationModal = get('phoneVerificationModal') as GlobalContextState['phoneVerificationModal'];
  const setPhoneVerificationModal = (phoneVerificationModal: GlobalContextState['phoneVerificationModal']) =>
    set({ phoneVerificationModal });

  const { t } = useTranslation();

  const {
    visible,
    onClose,
    type = UserPhoneVerificationType.VERIFY_NEW_PHONE,
    skipSuccessView,
    previousCode,
    prefillPhoneNumber,
    triggerSource,
  } = phoneVerificationModal || {};

  const [phone, setPhone] = useState<PhoneInputData>(null);
  const [view, setView] = useState<View>(View.SEND_CODE);

  const usedCodeRef = useRef(null);

  const title = {
    [UserPhoneVerificationType.CHANGE_PHONE_VERIFY_CURRENT_NUMBER]: t('components.phone-verification.edit-phone'),
    [UserPhoneVerificationType.CHANGE_PHONE_VERIFY_NEW_NUMBER]: t('components.phone-verification.edit-phone'),
    [UserPhoneVerificationType.VERIFY_NEW_PHONE]: t('components.phone-verification.verify-phone'),
    [UserPhoneVerificationType.VERIFY_ACTION]: t('components.phone-verification.verify'),
  }[type];

  const onSendCodeSuccess = () => {
    GTM.logEvent(GTMEvents.OTP_SENT, { trigger_source: triggerSource });
    setView(View.VERIFY_CODE);
  };

  const onReSendCodeSuccess = () => {
    GTM.logEvent(GTMEvents.OTP_SENT, { trigger_source: triggerSource, resend: true });
  };

  const onVerifyCodeSuccess = (usedCode: string) => {
    usedCodeRef.current = usedCode;
    GTM.logEvent(GTMEvents.OTP_VERIFIED, { trigger_source: triggerSource });

    if (skipSuccessView) {
      onVerificationFlowEnd(true);
    } else {
      setView(View.DONE);
    }
  };

  const onVerificationFlowEnd = (isSuccess = false) => {
    GTM.logEvent(GTMEvents.OTP_FORM_CLOSED, { trigger_source: triggerSource });

    setPhoneVerificationModal({
      ...phoneVerificationModal,
      visible: false,
    });

    onClose?.(isSuccess, usedCodeRef.current, phone?.raw);
  };

  useEffect(() => {
    if (visible) {
      setView(View.SEND_CODE);
      usedCodeRef.current = null;
      GTM.logEvent(GTMEvents.OTP_FORM_OPENED, { trigger_source: triggerSource });
    }
  }, [visible]);

  return (
    <Modal
      open={!!visible}
      maxWidth={'xs'}
      fullWidth
      onClose={() => onVerificationFlowEnd(false)}
      closeIconVisible={view !== View.DONE}
      disableBackdropDismiss
      className="user-verification-modal"
      title={view === View.VERIFY_CODE ? title : null}
      titleIcon={
        view === View.VERIFY_CODE ? (
          <IconButton onClick={() => setView(View.SEND_CODE)} size="small" className="user-verification-modal-back-button">
            <ArrowBackIcon />
          </IconButton>
        ) : null
      }
      titleUnderline={false}
    >
      <Grid container>
        <div className="user-verification-modal-header">
          <img
            className="user-verification-modal-image"
            src={
              view === View.DONE
                ? '/images/user-verification/user-verification-success.png'
                : '/images/user-verification/user-verification.png'
            }
            alt=""
          />
        </div>

        {view === View.SEND_CODE && (
          <UserPhoneVerificationSendCode
            type={type}
            prefillPhoneNumber={prefillPhoneNumber}
            phone={phone}
            setPhone={setPhone}
            onSuccess={onSendCodeSuccess}
            triggerSource={triggerSource}
          />
        )}

        {view === View.VERIFY_CODE && (
          <UserPhoneVerificationVerifyCode
            type={type}
            phone={phone}
            onSuccess={onVerifyCodeSuccess}
            onReSendCodeSuccess={onReSendCodeSuccess}
            previousCode={previousCode}
            triggerSource={triggerSource}
          />
        )}

        {view === View.DONE && <UserPhoneVerificationSuccess phone={phone} onDone={() => onVerificationFlowEnd(true)} />}
      </Grid>
    </Modal>
  );
};
